/**
 * Billing / Plan Catalog Service
 *
 * Wraps the token-billing plan-catalog endpoints (GitHub #172, Phase D).
 *   - `GET  /billing/plans`                       (authenticated) — visible, non-archived plans
 *   - `GET  /admin/subscription-plans`            (super-admin)   — ALL plans incl. archived/custom
 *   - `GET  /admin/subscription-plans/:planId`    (super-admin)
 *   - `PUT  /admin/subscription-plans/:planId`    (super-admin)
 *   - `POST /admin/subscription-plans`            (super-admin)   — create / clone
 *   - `POST /admin/subscription-plans/:planId/sync-stripe` (super-admin) — Phase E (501 today)
 */

import { apiRequest, apiRequestBlob } from '@/services/api';
import type { PlanDefinition } from '@simcheck/shared-types';

/** PlanDefinition as returned by the API — the Firestore doc id is included. */
export type PlanRecord = PlanDefinition & { id: string };

/** Fields accepted by the create endpoint. */
export interface CreatePlanBody {
  id?: string;
  cloneFrom?: string;
  customForOrgId?: string | null;
  displayName?: string;
  description?: string;
  public?: boolean;
  archived?: boolean;
  order?: number;
  entitlements?: Partial<PlanDefinition['entitlements']>;
  pricing?: Partial<PlanDefinition['pricing']>;
}

/** Fields accepted by the update endpoint. */
export interface UpdatePlanBody {
  displayName?: string;
  description?: string;
  public?: boolean;
  archived?: boolean;
  order?: number;
  entitlements?: Partial<PlanDefinition['entitlements']>;
  pricing?: Partial<PlanDefinition['pricing']>;
}

/** Public plan catalog — visible, non-archived plans (used by the upgrade modal). */
export async function getPlans(): Promise<PlanRecord[]> {
  const res = await apiRequest<{ plans: PlanRecord[] }>('/billing/plans', { method: 'GET' });
  return res.plans;
}

/** Super-admin: every plan incl. archived/custom, sorted by `order`. */
export async function getAdminPlans(): Promise<PlanRecord[]> {
  const res = await apiRequest<{ plans: PlanRecord[] }>('/admin/subscription-plans', { method: 'GET' });
  return res.plans;
}

/** Super-admin: a single plan doc. */
export async function getAdminPlan(id: string): Promise<PlanRecord> {
  return apiRequest<PlanRecord>(`/admin/subscription-plans/${encodeURIComponent(id)}`, { method: 'GET' });
}

/** Super-admin: update a plan; returns the updated doc. */
export async function updatePlan(id: string, body: UpdatePlanBody): Promise<PlanRecord> {
  return apiRequest<PlanRecord>(`/admin/subscription-plans/${encodeURIComponent(id)}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

/** Super-admin: create (or clone) a plan; returns the created doc (201). */
export async function createPlan(body: CreatePlanBody): Promise<PlanRecord> {
  return apiRequest<PlanRecord>('/admin/subscription-plans', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/** Super-admin: push a plan's pricing into Stripe. Returns 501 today (Phase E). */
export async function syncPlanToStripe(id: string): Promise<{ message?: string }> {
  return apiRequest<{ message?: string }>(`/admin/subscription-plans/${encodeURIComponent(id)}/sync-stripe`, {
    method: 'POST',
  });
}

// ============================================================================
// TOKEN ADD-ONS & INVOICES (customer-facing billing UI, Phase 1)
// ============================================================================

/**
 * A purchasable add-on from the public billing catalog. Card-purchasable only
 * when `stripePriceId` is set — otherwise it's wire/contact-us only.
 */
export interface BillingAddon {
  id: string;
  kind: 'token_pack' | 'esim' | 'seats' | 'other';
  billing: 'one_time' | 'recurring';
  displayName: string;
  description?: string;
  tokens?: number;
  priceUsd: number;
  currency: string;
  stripePriceId?: string;
  public: boolean;
  archived?: boolean;
  order?: number;
  pricing?: {
    syncStatus?: 'synced' | 'dirty' | 'error';
  };
}

/** Fields accepted by the admin add-on create/update endpoints. */
export interface BillingAddonBody {
  id?: string;
  kind?: BillingAddon['kind'];
  billing?: BillingAddon['billing'];
  displayName?: string;
  description?: string;
  tokens?: number;
  priceUsd?: number;
  currency?: string;
  public?: boolean;
  archived?: boolean;
  order?: number;
}

/** A single line on an invoice. */
export interface InvoiceLineItem {
  kind: string;
  description: string;
  quantity: number;
  unitAmountCents: number;
  amountCents: number;
  grantsTokens?: number;
}

/** An invoice in the active org's billing history. `createdAt`/`paidAt` are Firestore Timestamps over the wire. */
export interface Invoice {
  id: string;
  orgId: string;
  plan?: string;
  interval?: string;
  amountCents: number;
  currency: string;
  reference: string;
  status: 'open' | 'paid' | 'void';
  billingMethod: 'wire' | 'stripe' | 'manual' | 'invoice';
  lineItems?: InvoiceLineItem[];
  createdAt: any;
  dueAt?: any;
  paidAt?: any;
  txnId?: string;
}

/** Public add-on catalog (token packs, etc.). */
export async function getBillingAddons(orgId: string): Promise<BillingAddon[]> {
  const res = await apiRequest<{ addons: BillingAddon[] }>(
    '/billing/addons',
    { method: 'GET' },
    orgId,
  );
  return res.addons;
}

/** The active org's invoice history. */
export async function getInvoices(orgId: string): Promise<Invoice[]> {
  const res = await apiRequest<{ invoices: Invoice[] }>(
    '/billing/invoices',
    { method: 'GET' },
    orgId,
  );
  return res.invoices;
}

/** Download a wire invoice's PDF (the same document sent by email). */
export async function downloadInvoicePdf(orgId: string, invoice: Invoice): Promise<void> {
  const blob = await apiRequestBlob(`/billing/invoices/${invoice.id}/pdf`, orgId);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `Invoice-${invoice.reference}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** Start a Stripe Checkout session for a one-time token add-on; returns the redirect URL. */
export async function createTopupSession(orgId: string, addonId: string): Promise<{ url: string }> {
  return apiRequest<{ url: string }>(
    '/billing/create-topup-session',
    {
      method: 'POST',
      body: JSON.stringify({ addonId, returnBaseUrl: window.location.origin }),
    },
    orgId,
  );
}

// ============================================================================
// ADMIN: ADD-ON CATALOG, OVERAGE SETTINGS & PER-ORG OVERAGE (super-admin)
// ============================================================================

/** Super-admin: every add-on incl. hidden/archived, sorted by `order`. */
export async function getBillingAddonsAdmin(): Promise<BillingAddon[]> {
  const res = await apiRequest<{ addons: BillingAddon[] }>('/admin/billing-addons', { method: 'GET' });
  return res.addons;
}

/** Super-admin: create an add-on; returns the created doc. */
export async function createBillingAddon(body: BillingAddonBody): Promise<BillingAddon> {
  return apiRequest<BillingAddon>('/admin/billing-addons', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/** Super-admin: update an add-on; returns the updated doc. */
export async function updateBillingAddon(id: string, body: BillingAddonBody): Promise<BillingAddon> {
  return apiRequest<BillingAddon>(`/admin/billing-addons/${encodeURIComponent(id)}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

/** Super-admin: push an add-on's price into Stripe. May be skipped (no price set, etc.). */
export async function syncBillingAddon(
  id: string,
): Promise<{ addonId: string; priceId?: string; skipped?: boolean; reason?: string }> {
  return apiRequest<{ addonId: string; priceId?: string; skipped?: boolean; reason?: string }>(
    `/admin/billing-addons/${encodeURIComponent(id)}/sync-stripe`,
    { method: 'POST' },
  );
}

/** Super-admin: read the global overage price (USD per token). */
export async function getBillingSettings(): Promise<{ overagePricePerTokenUsd: number }> {
  return apiRequest<{ overagePricePerTokenUsd: number }>('/admin/billing-settings', { method: 'GET' });
}

/** Super-admin: update the global overage price (USD per token). */
export async function updateBillingSettings(
  overagePricePerTokenUsd: number,
): Promise<{ overagePricePerTokenUsd: number }> {
  return apiRequest<{ overagePricePerTokenUsd: number }>('/admin/billing-settings', {
    method: 'PUT',
    body: JSON.stringify({ overagePricePerTokenUsd }),
  });
}

/** Super-admin: toggle post-paid overage for a single org. */
export async function setOrgOverage(orgId: string, allowOverage: boolean): Promise<{ ok: boolean }> {
  return apiRequest<{ ok: boolean }>(`/admin/orgs/${encodeURIComponent(orgId)}/overage`, {
    method: 'PUT',
    body: JSON.stringify({ allowOverage }),
  });
}

// ============================================================================
// CUSTOMER: RECURRING ADD-ONS (org admin — requires a Stripe card subscription)
// ============================================================================

/** Org admin: attach a recurring add-on to the org's Stripe subscription. */
export async function addRecurringAddon(
  orgId: string,
  addonId: string,
  quantity: number,
): Promise<{ ok: boolean; stripeItemId: string }> {
  return apiRequest<{ ok: boolean; stripeItemId: string }>(
    '/billing/add-recurring-addon',
    {
      method: 'POST',
      body: JSON.stringify({ addonId, quantity }),
    },
    orgId,
  );
}

/** Org admin: remove a recurring add-on from the org's Stripe subscription. */
export async function removeRecurringAddon(
  orgId: string,
  stripeItemId: string,
): Promise<{ ok: boolean }> {
  return apiRequest<{ ok: boolean }>(
    '/billing/remove-recurring-addon',
    {
      method: 'POST',
      body: JSON.stringify({ stripeItemId }),
    },
    orgId,
  );
}
